import { cn } from "web-design-system";
import { CheckCheckIcon, LucideIndianRupee } from "lucide-react";
import Amount from "@funds/components/atoms/amount";
import { useMemo } from "react";
import { ConditionalRenderer } from "@core/components/conditional-renderer";

type AllocationStatus = "default" | "over-allocated" | "complete";

export function AllocationStatus<T extends string | number>({
  current,
  total,
}: {
  current: T;
  total: T;
}) {
  const percentage = useMemo(() => {
    return Math.round((Number(current) / Number(total)) * 100);
  }, [current, total]);

  return (
    <div className="flex flex-row gap-3 items-center">
      <AllocationStatusIcon percentage={percentage} />
      <div className="flex flex-col gap-1">
        <ConditionalRenderer condition={percentage < 100}>
          <span className="text-sm font-semibold">{percentage}% allocated</span>
          <span className="text-xs text-muted-foreground">
            <Amount precision={0}>{Number(total) - Number(current)}</Amount>{" "}
            remaining
          </span>
        </ConditionalRenderer>
        <ConditionalRenderer condition={percentage === 100}>
          <span className="text-sm font-semibold text-constructive">
            Allocation Complete
          </span>
          <span className="text-xs text-muted-foreground">
            <Amount precision={0}>{current}</Amount> {" / "}{" "}
            <Amount precision={0}>{total}</Amount>
          </span>
        </ConditionalRenderer>
        <ConditionalRenderer condition={percentage > 100}>
          <span className="text-sm font-semibold text-orange-500">
            {percentage - 100}% Excess allocated
          </span>
          <span className="text-xs text-muted-foreground">
            Additional{" "}
            <Amount precision={0}>{Number(current) - Number(total)}</Amount>
          </span>
        </ConditionalRenderer>
      </div>
    </div>
  );
}

function AllocationStatusIcon({
  percentage = 0,
  className,
}: {
  percentage?: number;
  className?: string;
}) {
  if (percentage === 100) {
    return (
      <div
        className={cn(
          "size-9 rounded-md bg-constructive-foreground border border-constructive flex items-center justify-center",
          className
        )}
      >
        <CheckCheckIcon className="size-5 text-constructive" strokeWidth={3} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative size-9 flex items-center justify-center rounded-md",
        className,
        percentage < 100 && "bg-secondary",
        percentage > 100 &&
          "bg-orange-50 text-orange-500 border border-orange-500"
      )}
    >
      <LucideIndianRupee className="size-5" />
    </div>
  );
}
